import { useState } from "react";
import { CREATIVE_PIPELINE, CR_COLORS } from "../constants";
import { fmtNum, fmtCurr } from "../utils";
import { Dot, Btn, Modal, Field, Inp, Sel, Txta, Empty, Card, FormRow, Stage, Chips, Selo, ExpandTable } from "../components/primitives";

// ── CriativoModal (ver/editar) ────────────────────────────────────────────────
function CriativoModal({ criativo, clients, onClose, onUpdate }) {
  const [f, setF]           = useState(criativo);
  const [saving, setSaving] = useState(false);
  const up  = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const num = (v) => v === "" ? null : parseFloat(v);

  async function save() {
    setSaving(true);
    await onUpdate(f.id, {
      nome: f.nome, tipo: f.tipo, angulo: f.angulo,
      plataforma: f.plataforma, status: f.status,
      hook: f.hook, copy: f.copy, cta: f.cta,
      objetivo: f.objetivo, publico: f.publico,
      roteiro: f.roteiro, resultado: f.resultado,
      insight: f.insight, proxima_acao: f.proxima_acao,
      thumbnail_url: f.thumbnail_url,
      ctr: num(f.ctr), cpc: num(f.cpc), cpa: num(f.cpa),
      observacoes: f.observacoes,
    });
    setSaving(false);
  }

  return (
    <Modal title={f.nome} subtitle={`Status: ${f.status}`} onClose={onClose} width={600}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <FormRow>
          <Field label="Nome" half><Inp value={f.nome} onChange={(e) => up("nome", e.target.value)} /></Field>
          <Field label="Status" half>
            <Sel value={f.status} onChange={(e) => up("status", e.target.value)}>
              {CREATIVE_PIPELINE.map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Tipo" third><Inp value={f.tipo || ""} onChange={(e) => up("tipo", e.target.value)} placeholder="Vídeo/Imagem..." /></Field>
          <Field label="Plataforma" third>
            <Sel value={f.plataforma || "Meta"} onChange={(e) => up("plataforma", e.target.value)}>
              {["Meta","Google","TikTok","Orgânico"].map((p) => <option key={p}>{p}</option>)}
            </Sel>
          </Field>
          <Field label="Cliente" third>
            <Sel value={f.client_id || ""} onChange={(e) => up("client_id", e.target.value)}>
              <option value="">— Sem cliente —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Hook"><Txta rows={2} value={f.hook || ""} onChange={(e) => up("hook", e.target.value)} /></Field>
        <Field label="Copy"><Txta rows={3} value={f.copy || ""} onChange={(e) => up("copy", e.target.value)} /></Field>
        <FormRow>
          <Field label="CTA" half><Inp value={f.cta || ""} onChange={(e) => up("cta", e.target.value)} /></Field>
          <Field label="Ângulo" half><Inp value={f.angulo || ""} onChange={(e) => up("angulo", e.target.value)} /></Field>
        </FormRow>
        <FormRow>
          <Field label="CTR (%)" third><Inp type="number" value={f.ctr ?? ""} onChange={(e) => up("ctr", e.target.value)} /></Field>
          <Field label="CPC (R$)" third><Inp type="number" value={f.cpc ?? ""} onChange={(e) => up("cpc", e.target.value)} /></Field>
          <Field label="CPA (R$)" third><Inp type="number" value={f.cpa ?? ""} onChange={(e) => up("cpa", e.target.value)} /></Field>
        </FormRow>
        <Field label="Resultado"><Txta rows={2} value={f.resultado || ""} onChange={(e) => up("resultado", e.target.value)} /></Field>
        <Field label="Insight"><Txta rows={2} value={f.insight || ""} onChange={(e) => up("insight", e.target.value)} /></Field>
        <Field label="Próxima ação"><Inp value={f.proxima_acao || ""} onChange={(e) => up("proxima_acao", e.target.value)} /></Field>
        <Field label="Thumbnail URL (Cloudinary)"><Inp value={f.thumbnail_url || ""} onChange={(e) => up("thumbnail_url", e.target.value)} placeholder="https://res.cloudinary.com/..." /></Field>
        {f.thumbnail_url && (
          <img src={f.thumbnail_url} alt="thumb" style={{ width: "100%", maxHeight: 200, objectFit: "contain", borderRadius: 8 }} />
        )}
        <Btn variant="accent" full onClick={save} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Btn>
      </div>
    </Modal>
  );
}

// ── CriativoFormModal (novo) ──────────────────────────────────────────────────
export function CriativoFormModal({ clients, clientId, onClose, onSave }) {
  const [f, setF] = useState({
    client_id: clientId || "", nome: "", tipo: "Vídeo", angulo: "",
    plataforma: "Meta", status: "Ideia", hook: "", copy: "", cta: "",
    objetivo: "", publico: "", thumbnail_url: "",
  });
  const up = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  async function save() {
    if (!f.nome.trim()) return;
    setSaving(true); setError("");
    try { await onSave(f); }
    catch (e) { setError("Erro ao salvar: " + (e.message || "tente novamente")); }
    finally { setSaving(false); }
  }

  return (
    <Modal title="Novo criativo" onClose={onClose} width={520}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <FormRow>
          <Field label="Nome" half><Inp value={f.nome} onChange={(e) => up("nome", e.target.value)} placeholder="Criativo #1..." /></Field>
          <Field label="Status" half>
            <Sel value={f.status} onChange={(e) => up("status", e.target.value)}>
              {CREATIVE_PIPELINE.map((s) => <option key={s}>{s}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Tipo" third>
            <Sel value={f.tipo} onChange={(e) => up("tipo", e.target.value)}>
              {["Vídeo","Imagem","Carrossel","Story","Reels"].map((t) => <option key={t}>{t}</option>)}
            </Sel>
          </Field>
          <Field label="Plataforma" third>
            <Sel value={f.plataforma} onChange={(e) => up("plataforma", e.target.value)}>
              {["Meta","Google","TikTok","Orgânico"].map((p) => <option key={p}>{p}</option>)}
            </Sel>
          </Field>
          <Field label="Cliente" third>
            <Sel value={f.client_id} onChange={(e) => up("client_id", e.target.value)}>
              <option value="">— Nenhum —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Sel>
          </Field>
        </FormRow>
        <Field label="Hook"><Txta rows={2} value={f.hook} onChange={(e) => up("hook", e.target.value)} /></Field>
        <Field label="CTA"><Inp value={f.cta} onChange={(e) => up("cta", e.target.value)} /></Field>
        <Field label="Thumbnail URL (Cloudinary)"><Inp value={f.thumbnail_url} onChange={(e) => up("thumbnail_url", e.target.value)} placeholder="https://res.cloudinary.com/..." /></Field>
        {error && <div style={{ fontSize: 12, color: "var(--danger)", padding: "6px 10px", background: "var(--danger-soft)", borderRadius: 6 }}>{error}</div>}
        <Btn variant="accent" full onClick={save} disabled={saving || !f.nome.trim()}>
          {saving ? "Salvando..." : "Criar criativo"}
        </Btn>
      </div>
    </Modal>
  );
}

// ── CriativosView (exported) ──────────────────────────────────────────────────
export function CriativosView({ criativos, clients, onNew, onUpdate }) {
  const [viewMode, setViewMode] = useState("kanban");
  const [filter,   setFilter]   = useState("Todos");
  const [selected, setSelected] = useState(null);

  const clientNames = clients.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});
  const filterTypes = ["Todos", ...new Set(clients.map((c) => c.name))];

  const visible = criativos.filter((cr) =>
    filter === "Todos" || clientNames[cr.client_id] === filter
  );

  return (
    <div className="view">
      <Stage
        path="criativos"
        title="Criativos em"
        hl="produção"
        lead={<>{criativos.length} cadastrados</>}
        actions={<><Btn sm variant="primary" onClick={onNew}>+ Novo</Btn></>}
        tabs={
          <div className="pill-tabs" role="tablist" aria-label="Modo de ver">
            {["kanban","galeria","tabela"].map((m) => (
              <button key={m} type="button" role="tab" aria-selected={viewMode === m} className={`pill-tab${viewMode === m ? " on" : ""}`} onClick={() => setViewMode(m)}>
                {m.charAt(0).toUpperCase() + m.slice(1)}
              </button>
            ))}
          </div>
        }
      />

      {/* Filtro por cliente */}
      <Chips
        label="Filtrar por cliente"
        style={{ marginBottom: 20 }}
        items={filterTypes.slice(0, 8).map((f) => ({ id: f, label: f, count: f === "Todos" ? criativos.length : criativos.filter((cr) => clientNames[cr.client_id] === f).length }))}
        active={filter}
        onChange={setFilter}
      />

      {/* Kanban */}
      {viewMode === "kanban" && (
        <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 12, alignItems: "flex-start" }}>
          {CREATIVE_PIPELINE.map((stage) => {
            const items = visible.filter((cr) => cr.status === stage);
            return (
              <div key={stage} style={{ minWidth: 200, width: 200, flexShrink: 0 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 7, marginBottom: 10,
                  padding: "6px 12px", borderRadius: "var(--r-pill)",
                  background: "var(--surface)", border: "1px solid var(--line-soft)",
                }}>
                  <Dot color={CR_COLORS[stage]} size={8} />
                  <span style={{ font: "600 12.5px var(--font-display)", color: "var(--ink)" }}>{stage}</span>
                  <span className="minibar-n" style={{ marginLeft: "auto" }}>{items.length}</span>
                </div>
                {items.map((cr) => (
                  <Card
                    key={cr.id}
                    hover
                    onClick={() => setSelected(cr)}
                    style={{ padding: "12px 14px", marginBottom: 8 }}
                  >
                    {cr.thumbnail_url && (
                      <img src={cr.thumbnail_url} alt={cr.nome} style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4, marginBottom: 8 }} />
                    )}
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>{cr.nome}</div>
                    {cr.hook && <div style={{ fontSize: 11, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cr.hook}</div>}
                    {clientNames[cr.client_id] && (
                      <div style={{ fontSize: 10, color: "var(--faint)", marginTop: 4 }}>{clientNames[cr.client_id]}</div>
                    )}
                  </Card>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Galeria */}
      {viewMode === "galeria" && (
        visible.length === 0 ? <Empty icon="🎬" text="Nenhum criativo encontrado." /> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14 }}>
            {visible.map((cr) => (
              <Card key={cr.id} hover onClick={() => setSelected(cr)} style={{ padding: "14px 16px" }}>
                {cr.thumbnail_url && (
                  <img src={cr.thumbnail_url} alt={cr.nome} style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, marginBottom: 10 }} />
                )}
                <div style={{ display: "flex", gap: 5, marginBottom: 6, alignItems: "center" }}>
                  <Selo tone="off">{cr.status}</Selo>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{cr.nome}</div>
                {cr.hook && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>{cr.hook}</div>}
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  {cr.ctr != null && <span style={{ fontSize: 11, color: "var(--faint)" }}>CTR {fmtNum(cr.ctr)}%</span>}
                  {cr.cpa != null && <span style={{ fontSize: 11, color: "var(--faint)" }}>CPA {fmtCurr(cr.cpa)}</span>}
                </div>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Tabela: uma linha por criativo, abre o modal */}
      {viewMode === "tabela" && (
        <ExpandTable
          rows={visible}
          onOpen={(cr) => setSelected(cr)}
          empty="Nenhum criativo encontrado."
          columns={[
            { key: "nome", label: "Nome", render: (cr) => <span className="name">{cr.nome}</span> },
            { key: "cliente", label: "Cliente", render: (cr) => clientNames[cr.client_id] || "—" },
            { key: "status", label: "Status", render: (cr) => <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Dot color={CR_COLORS[cr.status] || "var(--faint)"} size={7} />{cr.status}</span> },
            { key: "plataforma", label: "Plataforma", render: (cr) => cr.plataforma || "—" },
            { key: "ctr", label: "CTR", align: "right", render: (cr) => cr.ctr != null ? `${fmtNum(cr.ctr)}%` : "—" },
            { key: "cpa", label: "CPA", align: "right", render: (cr) => fmtCurr(cr.cpa) },
            { key: "cpc", label: "CPC", align: "right", render: (cr) => fmtCurr(cr.cpc) },
          ]}
        />
      )}

      {selected && (
        <CriativoModal
          criativo={selected}
          clients={clients}
          onClose={() => setSelected(null)}
          onUpdate={async (id, data) => {
            await onUpdate(id, data);
            setSelected(null);
          }}
        />
      )}
    </div>
  );
}
